
import React, { useState } from 'react';
import { useApiProviders } from '@/hooks/useApiProviders';
import { useUserIntegrations, useDeleteIntegration } from '@/hooks/useApiIntegrations';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { ConnectForm24Seven } from './provider-forms/ConnectForm24Seven';
import { LogsViewer } from './LogsViewer';
import { Plus, MoreVertical, Link2, Unlink, History, Check, Clock, AlertTriangle } from 'lucide-react';

export function UserConnections() {
  const { user } = useAuth();
  const { data: providers = [], isLoading: isLoadingProviders } = useApiProviders();
  const { data: integrations = [], isLoading: isLoadingIntegrations } = useUserIntegrations(user?.id);
  const { mutate: deleteIntegration } = useDeleteIntegration();
  
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [isConnectDialogOpen, setIsConnectDialogOpen] = useState(false);
  const [isLogsDialogOpen, setIsLogsDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<any>(null);
  
  const handleConnect = (provider: any) => {
    setSelectedProvider(provider);
    setIsConnectDialogOpen(true);
  };
  
  const handleViewLogs = (integration: any) => {
    setSelectedIntegration(integration);
    setIsLogsDialogOpen(true);
  };
  
  const handleDisconnect = (integration: any) => {
    if (window.confirm(`Are you sure you want to disconnect from ${integration.provider.name}?`)) {
      deleteIntegration({
        id: integration.id,
        user_id: user?.id || '',
        provider_id: integration.provider_id
      });
    }
  };
  
  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'connected':
        return <Check className="h-4 w-4 text-green-500" />;
      case 'expired':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };
  
  const getStatusText = (status?: string) => {
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'expired':
        return 'Token Expired';
      default:
        return 'Disconnected';
    }
  };
  
  if (isLoadingProviders || isLoadingIntegrations) {
    return <div className="flex justify-center p-6">Loading connections...</div>;
  }
  
  // Find providers without integrations
  const availableProviders = providers.filter(provider => 
    !integrations.some(integration => integration.provider_id === provider.id)
  );
  
  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Your API Connections</h2>
        <p className="text-muted-foreground">
          Connect with your ERP and other business systems to import and export data.
        </p>
      </div>
      
      {integrations.length === 0 ? (
        <Card className="border border-dashed p-6 mb-8">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">You don't have any active connections yet.</p>
            {availableProviders.length > 0 && (
              <Button onClick={() => handleConnect(availableProviders[0])}>
                <Plus className="mr-2 h-4 w-4" /> Connect to a Service
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-8">
          {integrations.map((integration) => (
            <Card key={integration.id}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-center">
                    <div className="mr-2">
                      {getStatusIcon(integration.connection_status)}
                    </div>
                    <div>
                      <CardTitle className="text-lg font-semibold">
                        {integration.provider?.name || 'Unknown Provider'}
                      </CardTitle>
                      <CardDescription>
                        {getStatusText(integration.connection_status)}
                      </CardDescription>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleConnect(integration.provider)}>
                        <Link2 className="mr-2 h-4 w-4" /> Re-connect
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleViewLogs(integration)}>
                        <History className="mr-2 h-4 w-4" /> View Logs
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDisconnect(integration)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Unlink className="mr-2 h-4 w-4" /> Disconnect
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="pb-2">
                <div className="flex flex-wrap gap-2 mb-2">
                  <Badge variant="outline">{integration.provider?.auth_type}</Badge>
                  {integration.last_synced_at && (
                    <Badge variant="secondary">
                      Last sync: {new Date(integration.last_synced_at).toLocaleDateString()}
                    </Badge>
                  )}
                </div>
                {integration.connection_status === 'expired' && (
                  <Alert variant="warning" className="mt-2">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Authentication expired</AlertTitle>
                    <AlertDescription>
                      Your connection requires re-authentication.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
              <CardFooter>
                <div className="w-full">
                  {integration.connection_status === 'expired' ? (
                    <Button 
                      onClick={() => handleConnect(integration.provider)}
                      className="w-full"
                    >
                      <Link2 className="mr-2 h-4 w-4" /> Re-connect
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      onClick={() => handleViewLogs(integration)}
                      className="w-full"
                    >
                      <History className="mr-2 h-4 w-4" /> View Request Logs
                    </Button>
                  )}
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
      
      {availableProviders.length > 0 && (
        <>
          <h3 className="text-lg font-medium mb-4">Available Providers</h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {availableProviders.map((provider) => (
              <Card key={provider.id} className="hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => handleConnect(provider)}>
                <CardHeader>
                  <CardTitle className="text-lg">{provider.name}</CardTitle>
                  <CardDescription>{provider.auth_type} authentication</CardDescription>
                </CardHeader>
                <CardFooter>
                  <Button variant="outline" className="w-full">
                    <Plus className="mr-2 h-4 w-4" /> Connect
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </>
      )}
      
      <Dialog open={isConnectDialogOpen} onOpenChange={setIsConnectDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Connect to {selectedProvider?.name}</DialogTitle>
          </DialogHeader>
          
          {selectedProvider?.auth_type === 'custom_token' && selectedProvider?.code === '24seven' && (
            <ConnectForm24Seven 
              provider={selectedProvider} 
              userId={user?.id || ''}
              onSuccess={() => setIsConnectDialogOpen(false)}
            />
          )}
          
          {/* Add more provider-specific forms here */}
          
          {!(selectedProvider?.auth_type === 'custom_token' && selectedProvider?.code === '24seven') && (
            <div className="p-4 text-center">
              <p className="text-muted-foreground mb-4">
                Connection form for this provider type is not yet implemented.
              </p>
              <Button onClick={() => setIsConnectDialogOpen(false)}>
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      <Dialog open={isLogsDialogOpen} onOpenChange={setIsLogsDialogOpen}>
        <DialogContent className="sm:max-w-[900px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Logs - {selectedIntegration?.provider?.name}</DialogTitle>
          </DialogHeader>
          
          {selectedIntegration && (
            <LogsViewer 
              userId={user?.id || ''} 
              providerId={selectedIntegration.provider_id}
            />
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLogsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
